import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Info, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type AgendaItem,
  getGetJobByIdQueryKey,
  getListAgendaItemsQueryKey,
  useCreateEncounter,
  useListAgendaItems,
} from "@elation/api-client/src/stitch-web";

import { AgendaItemEntity } from "@src/features/agenda/domain";
import { VisitTour } from "@src/features/tourguide";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import { Checkbox } from "@src/shared/components/ui/checkbox";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { SortableList } from "@src/shared/components/v2/SortableList";
import { cn } from "@src/shared/lib/utils";
import { events } from "@src/shared/utils/observability/events";

import { AgendaAddItemControls } from "../AgendaAddItemControls/AgendaAddItemControls";
import {
  type GapListUploadHandOffContext,
  GapListUploadRowV2,
} from "../GapListUploadRow/GapListUploadRow";
import {
  type AgendaItemMutationError,
  isPendingAgendaItem,
  useAgendaItemMutations,
} from "./useAgendaItemMutations";
import { useGapListUploads } from "./useGapListUploads";

export interface AgendaV2Props {
  encounterId?: number;
  patientId?: number;
  disabled?: boolean;
  className?: string;
}

function sameItemsByReference(
  left: readonly AgendaItem[],
  right: readonly AgendaItem[],
): boolean {
  return (
    left.length === right.length && left.every((item, i) => item === right[i])
  );
}

function reconcileOrderedItems(
  current: readonly AgendaItem[],
  incoming: readonly AgendaItem[],
  pendingCreateIds: ReadonlySet<number>,
  completedCreateIds: ReadonlyMap<number, number>,
): AgendaItem[] {
  const actionableItems = incoming.filter(AgendaItemEntity.isActionable);
  const incomingById = new Map(actionableItems.map((item) => [item.id, item]));
  const currentPersistedIds = new Set(
    current
      .filter((item) => !isPendingAgendaItem(item.id))
      .map((item) => item.id),
  );
  const completedCreateServerIds = new Set(completedCreateIds.values());
  const usedIncomingIds = new Set<number>();
  const nextItems = current
    .map((item) => {
      const completedCreateId = isPendingAgendaItem(item.id)
        ? completedCreateIds.get(item.id)
        : undefined;
      if (completedCreateId != null) {
        const incomingItem = incomingById.get(completedCreateId);
        if (incomingItem) {
          usedIncomingIds.add(incomingItem.id);
          return incomingItem;
        }
      }

      if (isPendingAgendaItem(item.id) && pendingCreateIds.has(item.id)) {
        const matchingCreatedItem = actionableItems.find(
          (incomingItem) =>
            !usedIncomingIds.has(incomingItem.id) &&
            !currentPersistedIds.has(incomingItem.id) &&
            incomingItem.description === item.description &&
            incomingItem.type === item.type,
        );

        if (matchingCreatedItem) {
          usedIncomingIds.add(matchingCreatedItem.id);
          return matchingCreatedItem;
        }

        return item;
      }

      const incomingItem = incomingById.get(item.id);
      if (incomingItem) {
        usedIncomingIds.add(incomingItem.id);
      }

      return (
        incomingItem ??
        (completedCreateServerIds.has(item.id) ? item : undefined)
      );
    })
    .filter((item): item is AgendaItem => Boolean(item));

  for (const item of actionableItems) {
    if (
      !usedIncomingIds.has(item.id) &&
      !nextItems.some((existing) => existing.id === item.id)
    ) {
      nextItems.push(item);
    }
  }

  return nextItems;
}

interface OrderedAgendaItemsState {
  encounterId: number | undefined;
  items: AgendaItem[];
}

function mutationErrorMessage(error: AgendaItemMutationError): string {
  if (error.status === 403) {
    return "You do not have permission to update this agenda.";
  }

  if (error.status === 404) {
    return "This agenda item no longer exists.";
  }

  if (error.verb === "create") {
    return "Unable to add the agenda item. Please try again.";
  }

  if (error.verb === "delete") {
    return "Unable to remove the agenda item. Please try again.";
  }

  return "Unable to update the agenda item. Please try again.";
}

function AgendaListSkeleton() {
  return (
    <div className="px-[18px] py-4" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          className="flex min-h-10 items-center gap-3 border-border border-b py-2"
          key={index}
        >
          <Skeleton className="size-5 rounded-sm" />
          <Skeleton className="size-4 rounded-full" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  );
}

interface AgendaRowProps {
  item: AgendaItem;
  canEdit: boolean;
  onToggle: (item: AgendaItem) => void;
  onDelete: (item: AgendaItem) => void;
}

function AgendaRow({ item, canEdit, onToggle, onDelete }: AgendaRowProps) {
  const isCompleted = AgendaItemEntity.isCompleted(item);
  const isPending = isPendingAgendaItem(item.id);
  const isInteractive = canEdit && !isPending;

  return (
    <div className="group flex min-w-0 items-center gap-3 pr-1">
      <Checkbox
        aria-label={`Mark ${item.description} ${
          isCompleted ? "incomplete" : "complete"
        }`}
        checked={isCompleted}
        className="size-[17px] rounded-full border-muted-foreground/40 shadow-none data-[state=checked]:border-success data-[state=checked]:bg-success data-[state=checked]:text-success-foreground"
        disabled={!isInteractive}
        onCheckedChange={() => onToggle(item)}
      />
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-[14px] leading-5 text-foreground",
          isCompleted
            ? "text-muted-foreground line-through decoration-muted-foreground/60"
            : null,
          isPending ? "text-muted-foreground" : null,
        )}
      >
        {item.description}
      </span>
      <Button
        aria-label={`Remove ${item.description}`}
        className="size-7 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
        disabled={!isInteractive}
        onClick={() => onDelete(item)}
        size="icon"
        type="button"
        variant="ghost"
      >
        <Trash2 aria-hidden="true" className="size-3.5" />
      </Button>
    </div>
  );
}

export function AgendaV2({
  encounterId,
  patientId,
  disabled = false,
  className,
}: AgendaV2Props) {
  const queryClient = useQueryClient();
  const { mutateAsync: createEncounter } = useCreateEncounter();
  const completedGapListJobsRef = useRef<Set<string> | null>(null);
  if (completedGapListJobsRef.current === null) {
    completedGapListJobsRef.current = new Set();
  }
  const completedGapListJobs = completedGapListJobsRef.current;
  const [orderedAgendaItems, setOrderedAgendaItems] =
    useState<OrderedAgendaItemsState>({
      encounterId: undefined,
      items: [],
    });
  const {
    uploads: gapListUploads,
    uploadFiles: uploadGapListFiles,
    removeUpload: removeGapListUpload,
    resetUploads: resetGapListUploads,
    isUploading: isGapListUploading,
  } = useGapListUploads({ patientId, encounterId });
  const canLoadAgenda = encounterId != null;
  const canEdit = !disabled && encounterId != null && patientId != null;
  const agendaItemsQuery = useListAgendaItems(encounterId ?? 0, {
    query: {
      enabled: canLoadAgenda,
      refetchInterval: encounterId != null && encounterId > 0 ? 5000 : false,
    },
  });
  const fetchedItems = useMemo(
    () => agendaItemsQuery.data?.data.items ?? [],
    [agendaItemsQuery.data],
  );
  const actionableFetchedItems = useMemo(
    () => fetchedItems.filter(AgendaItemEntity.isActionable),
    [fetchedItems],
  );
  const {
    createAgendaItem,
    toggleAgendaItem,
    deleteAgendaItem,
    error,
    pendingCreateIds,
    completedCreateIds,
  } = useAgendaItemMutations({ encounterId, patientId });
  const pendingCreateIdSet = useMemo(
    () => new Set<number>(pendingCreateIds),
    [pendingCreateIds],
  );
  const orderedItems =
    orderedAgendaItems.encounterId === encounterId &&
    (orderedAgendaItems.items.length > 0 || actionableFetchedItems.length === 0)
      ? orderedAgendaItems.items
      : actionableFetchedItems;

  useEffect(() => {
    setOrderedAgendaItems((current) => {
      const currentItems =
        current.encounterId === encounterId ? current.items : [];
      const next = reconcileOrderedItems(
        currentItems,
        fetchedItems,
        pendingCreateIdSet,
        completedCreateIds,
      );

      if (
        current.encounterId === encounterId &&
        sameItemsByReference(current.items, next)
      ) {
        return current;
      }

      return { encounterId, items: next };
    });
  }, [encounterId, fetchedItems, pendingCreateIdSet, completedCreateIds]);

  useEffect(() => {
    completedGapListJobs.clear();
    resetGapListUploads();
  }, [patientId, encounterId, resetGapListUploads]);

  const handleRemoveGapListUpload = useCallback(
    (uploadId: string) => {
      removeGapListUpload(uploadId);
    },
    [removeGapListUpload],
  );

  const handleGapListJobComplete = useCallback(
    async ({
      jobId,
      patientId: uploadPatientId,
      encounterId: uploadEncounterId,
    }: GapListUploadHandOffContext) => {
      if (completedGapListJobs.has(jobId)) return;
      completedGapListJobs.add(jobId);

      const jobData = queryClient.getQueryData(getGetJobByIdQueryKey(jobId)) as
        | { data?: { metadata?: { opportunityIds?: number[] } } }
        | undefined;
      const opportunityIds = jobData?.data?.metadata?.opportunityIds ?? [];

      try {
        // Same hand-off v1 uses when opening Today: createEncounter runs
        // assignToEncounter for any open, unassigned agenda items.
        const encounterResult = await createEncounter({
          data: { patientId: uploadPatientId },
        });
        const assignedEncounterId = encounterResult.data.id;

        await queryClient.invalidateQueries({
          queryKey: getListAgendaItemsQueryKey(assignedEncounterId),
        });

        const routeEncounterId = uploadEncounterId ?? encounterId;
        if (
          routeEncounterId != null &&
          routeEncounterId !== assignedEncounterId
        ) {
          await queryClient.invalidateQueries({
            queryKey: getListAgendaItemsQueryKey(routeEncounterId),
          });
        }

        events.careGapsExtracted.track({
          patient_id: uploadPatientId,
          count: opportunityIds.length,
        });
      } catch (err) {
        completedGapListJobs.delete(jobId);
        throw err;
      }
    },
    [createEncounter, encounterId, queryClient],
  );

  const handleOrderedItemsChange = (items: AgendaItem[]) => {
    setOrderedAgendaItems({ encounterId, items });
  };

  const handleGapListFilesSelected = (files: File[]) => {
    uploadGapListFiles(files);
  };

  const handleToggle = (item: AgendaItem) => {
    if (!canEdit || isPendingAgendaItem(item.id)) return;
    void toggleAgendaItem(item.id, item.status, item.signalState);
  };

  const handleDelete = (item: AgendaItem) => {
    if (!canEdit || isPendingAgendaItem(item.id)) return;
    void deleteAgendaItem(item.id);
  };

  const hasDisplayableRows =
    orderedItems.length > 0 || gapListUploads.length > 0;
  const showInitialLoading = canLoadAgenda && agendaItemsQuery.isLoading;
  const showBlockingError =
    canLoadAgenda && agendaItemsQuery.isError && !hasDisplayableRows;
  const showRefreshError =
    canLoadAgenda && agendaItemsQuery.isError && hasDisplayableRows;
  const emptyMessage = canEdit
    ? "No items yet. Add your first below, or speak / drop a gap list."
    : patientId == null
      ? "No items yet. Select a patient first, then add agenda items."
      : "No items yet. Start a visit before adding agenda items.";

  return (
    <section
      aria-label="Visit agenda"
      className={cn("flex min-h-0 flex-1 flex-col bg-background", className)}
    >
      <VisitTour.AgendaItemsStep className="min-h-0 flex-1">
        <div className="h-full overflow-y-auto">
          <div className="px-[18px] pt-4">
            <div className="flex items-center gap-1.5">
              <h1 className="text-[16px] font-semibold tracking-[-0.02em] text-foreground">
                Visit agenda
              </h1>
              <Info
                aria-hidden="true"
                className="size-4 text-muted-foreground"
              />
              <span className="sr-only">
                Add items by typing, speaking, or dropping a gap list. They can
                be checked off during the visit.
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              What you want to cover this visit.
            </p>
          </div>

          {showInitialLoading ? (
            <AgendaListSkeleton />
          ) : showBlockingError ? (
            <div className="px-[18px] py-4">
              <Alert variant="destructive">
                <AlertCircle aria-hidden="true" className="size-4" />
                <AlertTitle>Unable to load agenda</AlertTitle>
                <AlertDescription>
                  Refresh the page or try again in a moment.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="px-[18px] py-4">
              {error ? (
                <Alert className="mb-3" variant="destructive">
                  <AlertCircle aria-hidden="true" className="size-4" />
                  <AlertTitle>Agenda update failed</AlertTitle>
                  <AlertDescription>
                    {mutationErrorMessage(error)}
                  </AlertDescription>
                </Alert>
              ) : null}
              {showRefreshError ? (
                <Alert className="mb-3" variant="destructive">
                  <AlertCircle aria-hidden="true" className="size-4" />
                  <AlertTitle>Agenda refresh failed</AlertTitle>
                  <AlertDescription>
                    Showing the most recent agenda items. We will try again
                    automatically.
                  </AlertDescription>
                </Alert>
              ) : null}

              {orderedItems.length > 0 ? (
                <SortableList
                  className="bg-background"
                  disabled={!canEdit}
                  getHandleLabel={(item) => `Reorder ${item.description}`}
                  getItemId={(item) => item.id}
                  getItemLabel={(item) =>
                    AgendaItemEntity.listItemLabel(item) ?? item.description
                  }
                  handleClassName="size-7"
                  itemClassName="min-h-10 gap-1 px-0 py-0"
                  items={orderedItems}
                  listLabel="Visit agenda items"
                  onItemsChange={handleOrderedItemsChange}
                  renderItem={({ item }) => (
                    <AgendaRow
                      canEdit={canEdit}
                      item={item}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                    />
                  )}
                />
              ) : gapListUploads.length === 0 ? (
                <p className="py-4 text-sm text-muted-foreground">
                  {emptyMessage}
                </p>
              ) : null}

              {gapListUploads.length > 0 ? (
                <ul aria-label="Gap list uploads" className="mt-3">
                  {gapListUploads.map((upload) => (
                    <GapListUploadRowV2
                      key={`${upload.id}:${upload.jobId ?? "pending"}`}
                      onComplete={handleGapListJobComplete}
                      onRemove={handleRemoveGapListUpload}
                      upload={upload}
                    />
                  ))}
                </ul>
              ) : null}
            </div>
          )}
        </div>
      </VisitTour.AgendaItemsStep>

      <AgendaAddItemControls
        canEdit={canEdit}
        encounterId={encounterId}
        isGapListUploading={isGapListUploading}
        patientId={patientId}
        onCreateAgendaItem={createAgendaItem}
        onGapListFilesSelected={handleGapListFilesSelected}
      />
    </section>
  );
}
